import { portalText, usePortalTranslation } from "../i18n/portal.js";
/**
 * The customer-facing half of design §8.7: when a WeTalk operator is acting as you, you see it.
 *
 * `self` renders nothing — the same rule the billing alert follows, for the same reason: a
 * banner that is always there is a banner nobody reads.
 *
 * Three things this component deliberately does NOT have:
 *
 *   - **A close control.** The operator console's banner has none either. A notice the subject
 *     can dismiss is not a notice; it is a preference.
 *   - **A stored `isImpersonating` flag.** It is driven entirely by the session query, so no
 *     component can forget to set one, and a grant ended in another tab disappears on the next
 *     tick rather than lingering until a reload.
 *   - **Any power to end the grant.** Ending it belongs to the operator who started it and to
 *     the server that bounds it. Offering the customer a button that cannot actually revoke
 *     anything would be worse than offering nothing.
 *
 * The countdown is derived during render from `expiresAt` rather than stored, so a clock that
 * has drifted or a tab that has slept cannot leave a stale number on screen.
 */
import type { ReactNode } from "react";

import { GLYPH } from "./glyph.js";
import { Icon } from "@wetalk/ui";
import type { AuthSessionActorView } from "../api/gateway.js";

export interface ImpersonationBannerProps {
  readonly actor: AuthSessionActorView;
  readonly subjectDisplayName: string;
  readonly nowMs: number;
}

/** Whole minutes remaining, floored, never negative. An expired grant reads "expiring now". */
export function minutesRemaining(expiresAt: string, nowMs: number): number {
  const remaining = Date.parse(expiresAt) - nowMs;
  if (!Number.isFinite(remaining) || remaining <= 0) return 0;
  return Math.floor(remaining / 60_000);
}

export function ImpersonationBanner({
  actor,
  subjectDisplayName,
  nowMs,
}: ImpersonationBannerProps): ReactNode {
  usePortalTranslation("shell");
  if (actor.kind === "self") return null;

  const minute = minutesRemaining(actor.expiresAt, nowMs);

  return (
    <div className="c-impersonation" role="status" aria-live="polite">
      <Icon glyph={GLYPH.warningCircle} />
      <p className="c-impersonation-line">
        <strong>{actor.operatorDisplayName}</strong> {portalText("shell", "from_wetalk_support_is_signed_in_as_62d03356")}{" "}
        <strong>{subjectDisplayName}</strong>{portalText("shell", "anything_done_now_is_recorded_against_them_not_you_and_app_dfaed484") + " "}</p>
      <p className="c-impersonation-meta">
        <span className="c-impersonation-reason">{portalText("shell", "reason_given_b62fb966")}{actor.reason}”</span>
        <span className="c-impersonation-expiry">
          {minute === 0 ? portalText("shell", "expiring_now_d73759db") : portalText("shell", "ends_in_value_min_2757e12b", { v0: String(String(minute)) })}
        </span>
      </p>
    </div>
  );
}
